import { IconCheck, IconRefresh } from "@tabler/icons-react";
import { useState } from "react";
import { ApiError, staffApi, type NoticeLogEntry, type NoticeRule, type OrderEventKind, type TimelineItem, type TripMember, type TripSummary } from "../api";
import { formatDateTime, relativeTime } from "../format";
import { useStaffData } from "./staffData";

const describe = (err: unknown) => (err instanceof ApiError ? `${err.code}${err.message && err.message !== err.code ? `: ${err.message}` : ""}` : err instanceof Error ? err.message : "failed");

/** Shared by the tab and the trip detail: one notice-log table. */
function LogTable({ log, withTrip }: { log: NoticeLogEntry[]; withTrip: boolean }) {
  if (log.length === 0) return <p className="muted small">No notices logged yet.</p>;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            {withTrip && <th>Trip</th>}
            <th>Rule</th>
            <th>Occurrence</th>
            <th>Recipient</th>
            <th>Scheduled</th>
            <th>Sent</th>
            <th>Channel</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {log.map((l, i) => (
            <tr key={l.id ?? `${l.tripId}:${l.ruleKey}:${l.occurrence}:${l.recipientEmail}:${i}`}>
              {withTrip && (
                <td>
                  <code className="small">{l.tripId.slice(0, 8)}</code>
                </td>
              )}
              <td>{l.ruleKey}</td>
              <td className="cell-clip">{l.occurrence}</td>
              <td>{l.recipientEmail}</td>
              <td>{formatDateTime(l.scheduledFor, "en")}</td>
              <td>{l.sentAt ? relativeTime(l.sentAt, "en") : "—"}</td>
              <td>{l.channel ?? "—"}</td>
              <td>
                <span className={`chip chip-${l.status}`}>{l.status}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Notices tab ───────────────────────────────────────────────────────────────

/** The rule table (enabled, local time) and the recent log; "run due" fires what the hourly job would. */
export function NoticesTab({ onError }: { onError: (err: unknown) => void }) {
  const rules = useStaffData(() => staffApi.noticeRules(), onError, []);
  const log = useStaffData(() => staffApi.noticesLog(), onError, []);
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const update = async (rule: NoticeRule, body: { enabled?: boolean; localTime?: string | null }) => {
    setBusy(rule.key);
    setError(null);
    try {
      await staffApi.updateNoticeRule(rule.key, body);
      rules.reload();
    } catch (err) {
      onError(err);
      setError(describe(err));
    } finally {
      setBusy(null);
    }
  };

  const runDue = async () => {
    setBusy("run");
    setError(null);
    try {
      const res = await staffApi.runNotices();
      setResult(Object.entries(res).map(([k, v]) => `${k}: ${v}`).join(" · ") || "done");
      log.reload();
    } catch (err) {
      onError(err);
      setError(describe(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="stack">
      <div className="section-title">
        <h2>Notice rules ({rules.data?.rules.length ?? 0})</h2>
        <div className="row-buttons">
          {result && <span className="muted small">{result}</span>}
          <button type="button" className="button button-small button-primary" disabled={busy !== null} onClick={() => void runDue()}>
            {busy === "run" ? "Running…" : "Run due notices now"}
          </button>
          <button type="button" className="icon-button" onClick={rules.reload} aria-label="Refresh rules">
            <IconRefresh size={18} />
          </button>
        </div>
      </div>
      <p className="muted small">Scheduled rules fire at their local time in the trip's zone; event rules fire from the live status events. Changes apply to the next run.</p>
      {(rules.error || error) && <p className="error-note">{rules.error ?? error}</p>}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Enabled</th>
              <th>Key</th>
              <th>Trigger</th>
              <th>Offset</th>
              <th>Local time</th>
              <th>Channels</th>
              <th>Audience</th>
              <th>Template</th>
            </tr>
          </thead>
          <tbody>
            {(rules.data?.rules ?? []).map((r) => (
              <tr key={r.key} className={r.enabled ? "" : "muted"}>
                <td>
                  <input type="checkbox" checked={r.enabled} disabled={busy !== null} onChange={(e) => void update(r, { enabled: e.target.checked })} aria-label={`${r.key} enabled`} />
                </td>
                <td>
                  <code className="small">{r.key}</code>
                </td>
                <td>{r.trigger}</td>
                <td>{r.offsetMinutes === 0 ? "—" : `${r.offsetMinutes > 0 ? "+" : "−"}${Math.abs(r.offsetMinutes) % 1440 === 0 ? `${Math.abs(r.offsetMinutes) / 1440} d` : `${Math.abs(r.offsetMinutes)} min`}`}</td>
                <td>
                  {r.trigger === "event" ? (
                    <span className="muted small">on event</span>
                  ) : (
                    <input type="time" className="inline-input" value={r.localTime ?? ""} disabled={busy !== null} onChange={(e) => void update(r, { localTime: e.target.value || null })} aria-label={`${r.key} local time`} />
                  )}
                </td>
                <td>{r.channelPolicy.join(" → ")}</td>
                <td>{r.audience}</td>
                <td>{r.template}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="section-title">
        <h2>Recent notices ({log.data?.log.length ?? 0})</h2>
        <button type="button" className="icon-button" onClick={log.reload} aria-label="Refresh log">
          <IconRefresh size={18} />
        </button>
      </div>
      {log.error && <p className="error-note">{log.error}</p>}
      {log.data && <LogTable log={log.data.log} withTrip />}
    </section>
  );
}

// ── Trip detail: notice sequence ──────────────────────────────────────────────

/** The planned sequence of a trip with a check when the log has it, and the two "time travel" buttons. */
export function TripNoticesSection({ trip, onError }: { trip: TripSummary; onError: (err: unknown) => void }) {
  const { data, error, reload } = useStaffData(() => staffApi.tripNotices(trip.id), onError, [trip.id]);
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const run = async (all: boolean) => {
    setBusy(all ? "all" : "due");
    setActionError(null);
    try {
      const res = await staffApi.runTripNotices(trip.id, all);
      setResult(`planned ${res.planned} · sent ${res.sent}`);
      reload();
    } catch (err) {
      onError(err);
      setActionError(describe(err));
    } finally {
      setBusy(null);
    }
  };

  const sentFor = (ruleKey: string, occurrence: string) => (data?.log ?? []).filter((l) => l.ruleKey === ruleKey && l.occurrence === occurrence && l.status === "sent");

  return (
    <>
      <div className="section-title">
        <h3>Notice sequence</h3>
        <div className="row-buttons">
          {result && <span className="muted small">{result}</span>}
          <button type="button" className="button button-small button-primary" disabled={busy !== null} onClick={() => void run(false)}>
            {busy === "due" ? "Sending…" : "Send due now"}
          </button>
          <button type="button" className="button button-small" disabled={busy !== null} onClick={() => void run(true)}>
            {busy === "all" ? "Sending…" : "Send the whole sequence now (demo)"}
          </button>
          <button type="button" className="icon-button" onClick={reload} aria-label="Refresh notices">
            <IconRefresh size={18} />
          </button>
        </div>
      </div>
      {(error || actionError) && <p className="error-note">{error ?? actionError}</p>}
      {data && data.sequence.length === 0 && <p className="muted small">Nothing scheduled: the trip needs dates and a confirmed status (proposals, pending and cancelled trips get no notices).</p>}
      {data && data.sequence.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Sent</th>
                <th>Rule</th>
                <th>Local day</th>
                <th>At ({trip.timezone})</th>
                <th>Items</th>
              </tr>
            </thead>
            <tbody>
              {data.sequence.map((n) => {
                const sent = sentFor(n.ruleKey, n.occurrence);
                return (
                  <tr key={`${n.ruleKey}:${n.occurrence}`} className={`sequence-row ${sent.length ? "sent" : ""}`}>
                    <td>{sent.length ? <span className="sequence-check" title={sent.map((l) => `${l.recipientEmail} · ${l.channel ?? ""}`).join("\n")}><IconCheck size={16} /> {sent.length}</span> : "—"}</td>
                    <td>
                      <code className="small">{n.ruleKey}</code>
                    </td>
                    <td>{n.localDay}</td>
                    <td>{formatDateTime(n.at, "en", trip.timezone)}</td>
                    <td className="cell-clip">{n.items.join(" · ") || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {data && data.log.length > 0 && (
        <details>
          <summary className="muted small">Notice log ({data.log.length})</summary>
          <LogTable log={data.log} withTrip={false} />
        </details>
      )}
    </>
  );
}

// ── Trip detail: live status simulator ───────────────────────────────────────

/**
 * Live status of a trip: flights as the Flights app reported them (it posts to /api/internal/events/*); the events
 * of apps that do not exist yet (Transfers) and of the platform order are fired from here (routes/companion.ts).
 */
export function LiveSimulator({ trip, timeline, onChanged, onError }: { trip: TripSummary; timeline: TimelineItem[]; onChanged: () => void; onError: (err: unknown) => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const run = async (key: string, fn: () => Promise<unknown>, label: string) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
      setDone(label);
      onChanged();
    } catch (err) {
      onError(err);
      setError(describe(err));
    } finally {
      setBusy(null);
    }
  };

  const flights = timeline.filter((i) => i.kind === "flight");
  const transfers = timeline.filter((i) => i.kind === "transfer");

  return (
    <>
      <div className="section-title">
        <h3>Live status</h3>
        {done && (
          <span className="muted small">
            <IconCheck size={14} /> {done}
          </span>
        )}
      </div>
      <p className="muted small">Flights are reported by the Flights app. The transfer and order events below are simulated here (no Transfers app yet): each one updates the timeline item and sends the matching notice to the members. Times are local to the item's zone.</p>
      {error && <p className="error-note">{error}</p>}
      {flights.length === 0 && transfers.length === 0 && <p className="muted small">No flight or transfer items on this timeline.</p>}
      <div className="stack">
        {flights.map((item) => (
          <FlightStatus key={item.id} trip={trip} item={item} />
        ))}
        {transfers.map((item) => (
          <TransferForm key={item.id} trip={trip} item={item} busy={busy} run={run} />
        ))}
        <OrderForm trip={trip} busy={busy} run={run} />
      </div>
    </>
  );
}

type Run = (key: string, fn: () => Promise<unknown>, label: string) => Promise<void>;

/** Flights report themselves now: the Flights app posts schedule changes, ticketing and check-in to the area. */
function FlightStatus({ trip, item }: { trip: TripSummary; item: TimelineItem }) {
  const tz = item.tz || trip.timezone;
  const ticketing = item.payload.ticketing as { tickets?: { number: string }[]; at?: string } | undefined;
  const checkIn = item.payload.checkIn as { at?: string } | undefined;
  const change = item.payload.scheduleChange as { at?: string } | undefined;
  return (
    <div className="sim-block">
      <p className="small">
        <strong>Flight</strong> · {item.title} <span className="muted">· {item.status} · {tz}</span>
      </p>
      <p className="muted small">
        From the Flights app: {change?.at ? `schedule change ${change.at.slice(0, 16).replace("T", " ")}` : "no schedule change"} · {ticketing?.tickets?.length ? `ticketed (${ticketing.tickets.map((t) => t.number).join(", ")})` : "not ticketed yet"} · {checkIn?.at ? `check-in told ${checkIn.at.slice(0, 16).replace("T", " ")}` : "check-in not told yet"}
      </p>
    </div>
  );
}

function TransferForm({ trip, item, busy, run }: { trip: TripSummary; item: TimelineItem; busy: string | null; run: Run }) {
  const existing = item.payload.driver && typeof item.payload.driver === "object" ? (item.payload.driver as Record<string, unknown>) : {};
  const s = (v: unknown) => (typeof v === "string" ? v : "");
  const [driver, setDriver] = useState(s(existing.name) || "João Silva");
  const [vehicle, setVehicle] = useState(s(existing.vehicle) || "Mercedes V-Class");
  const [plate, setPlate] = useState(s(existing.plate) || "AA-12-BB");
  const [phone, setPhone] = useState(s(existing.phone) || "+351 912 345 678");
  const [liveUrl, setLiveUrl] = useState(s(existing.liveUrl) || "https://maps.example.com/live/demo");
  const optional = () => ({ ...(vehicle.trim() ? { vehicle: vehicle.trim() } : {}), ...(plate.trim() ? { plate: plate.trim() } : {}), ...(phone.trim() ? { phone: phone.trim() } : {}) });
  return (
    <div className="sim-block">
      <p className="small">
        <strong>Transfer</strong> · {item.title} <span className="muted">· {item.status}</span>
      </p>
      <div className="row-buttons">
        <input className="inline-input" value={driver} onChange={(e) => setDriver(e.target.value)} placeholder="Driver" required />
        <input className="inline-input" value={vehicle} onChange={(e) => setVehicle(e.target.value)} placeholder="Vehicle" />
        <input className="inline-input" value={plate} onChange={(e) => setPlate(e.target.value)} placeholder="Plate" />
        <input className="inline-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone" />
        <input className="inline-input grow" value={liveUrl} onChange={(e) => setLiveUrl(e.target.value)} placeholder="Live URL (on the way)" type="url" />
      </div>
      <div className="row-buttons">
        <button type="button" className="button button-small" disabled={busy !== null || !driver.trim()} onClick={() => void run(`${item.id}:assigned`, () => staffApi.transferDriver({ tripId: trip.id, itemId: item.id, status: "assigned", driver: driver.trim(), ...optional() }), `Driver assigned: ${item.title}`)}>
          {busy === `${item.id}:assigned` ? "…" : "Assign driver"}
        </button>
        <button type="button" className="button button-small button-primary" disabled={busy !== null || !driver.trim()} onClick={() => void run(`${item.id}:en_route`, () => staffApi.transferDriver({ tripId: trip.id, itemId: item.id, status: "en_route", driver: driver.trim(), ...optional(), ...(liveUrl.trim() ? { liveUrl: liveUrl.trim() } : {}) }), `Driver on the way: ${item.title}`)}>
          {busy === `${item.id}:en_route` ? "…" : "Driver on the way"}
        </button>
      </div>
    </div>
  );
}

function OrderForm({ trip, busy, run }: { trip: TripSummary; busy: string | null; run: Run }) {
  const [documentsUrl, setDocumentsUrl] = useState("https://documents.example.com/demo.pdf");
  const [change, setChange] = useState("Your hotel room was upgraded to sea view.");
  const fire = (kind: OrderEventKind) =>
    run(`order:${kind}`, () => staffApi.orderEvent({ tripId: trip.id, kind, ...(kind === "documents_issued" && documentsUrl.trim() ? { documentsUrl: documentsUrl.trim() } : {}), ...(kind === "changed" && change.trim() ? { change: change.trim() } : {}) }), `Order event: ${kind}`);
  return (
    <div className="sim-block">
      <p className="small">
        <strong>Order</strong> · platform events
      </p>
      <div className="row-buttons">
        <input className="inline-input grow" type="url" value={documentsUrl} onChange={(e) => setDocumentsUrl(e.target.value)} placeholder="Documents URL" />
        <button type="button" className="button button-small" disabled={busy !== null} onClick={() => void fire("documents_issued")}>
          {busy === "order:documents_issued" ? "…" : "Documents issued"}
        </button>
      </div>
      <div className="row-buttons">
        <input className="inline-input grow" value={change} onChange={(e) => setChange(e.target.value)} placeholder="What changed" maxLength={500} />
        <button type="button" className="button button-small" disabled={busy !== null} onClick={() => void fire("changed")}>
          {busy === "order:changed" ? "…" : "Order changed"}
        </button>
        <button type="button" className="button button-small" disabled={busy !== null} onClick={() => window.confirm(`Mark ${trip.reference} as cancelled? Payment links are revoked.`) && void fire("cancelled")}>
          {busy === "order:cancelled" ? "…" : "Order cancelled"}
        </button>
      </div>
    </div>
  );
}

// ── Trip detail: members ──────────────────────────────────────────────────────

export function MembersSection({ trip, members, onError }: { trip: TripSummary; members: (TripMember & { travellerSlot?: number | null })[]; onError: (err: unknown) => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const resend = async (m: TripMember) => {
    setBusy(m.id);
    try {
      const res = await staffApi.resendInvite(trip.id, m.id);
      setResult(`${m.email ?? m.id}: ${res.sent} sent`);
    } catch (err) {
      onError(err);
      setResult(describe(err));
    } finally {
      setBusy(null);
    }
  };
  return (
    <>
      <div className="section-title">
        <h3>Members ({members.length})</h3>
        {result && <span className="muted small">{result}</span>}
      </div>
      {members.length === 0 ? (
        <p className="muted small">No member rows yet: the holder alone ({trip.holderName ?? "—"}).</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Role</th>
                <th>Name</th>
                <th>Email</th>
                <th>Slot</th>
                <th>Joined</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id}>
                  <td>{m.role}</td>
                  <td>{m.name ?? "—"}</td>
                  <td>{m.email ?? "—"}</td>
                  <td>{m.travellerSlot ?? "—"}</td>
                  <td>{m.joinedAt ? relativeTime(m.joinedAt, "en") : <span className="chip chip-invited">invited</span>}</td>
                  <td>
                    {m.role === "companion" && (
                      <button type="button" className="button button-small" disabled={busy !== null} onClick={() => void resend(m)}>
                        {busy === m.id ? "…" : "Resend invitation"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
